import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../services/api';

const PAGE_SIZE = 25;

const formatDateTime = (iso) => {
  if (!iso) return '–';
  try {
    return new Date(iso).toLocaleString();
  } catch (_) {
    return String(iso);
  }
};

const AdminChatsPage = () => {
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState(() => searchParams.get('search') || '');
  const [projectId, setProjectId] = useState(() => searchParams.get('project_id') || '');
  const [userId, setUserId] = useState(() => searchParams.get('user_id') || '');
  const [includeArchived, setIncludeArchived] = useState(() => {
    const value = searchParams.get('include_archived');
    return value === null ? true : value !== 'false';
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-chats', { page, search, projectId, userId, includeArchived }],
    queryFn: async () => {
      const params = {
        page,
        page_size: PAGE_SIZE,
        include_archived: includeArchived,
      };
      if (search.trim()) params.search = search.trim();
      if (projectId.trim()) params.project_id = projectId.trim();
      if (userId.trim()) params.user_id = userId.trim();
      const response = await adminAPI.getChats(params);
      return response.data;
    },
    keepPreviousData: true,
  });

  const items = data?.items || [];
  const pagination = data?.pagination || { page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 0 };

  const canGoPrev = useMemo(() => pagination.page > 1, [pagination.page]);
  const canGoNext = useMemo(
    () => pagination.totalPages && pagination.page < pagination.totalPages,
    [pagination.page, pagination.totalPages]
  );

  return (
    <div className="space-y-6 text-gray-900 dark:text-gray-100">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Sessions</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Browse chat sessions across all projects.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Search
          </label>
          <input
            type="text"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Filter by chat title, user, or project"
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Project ID
          </label>
          <input
            type="text"
            value={projectId}
            onChange={(e) => {
              setProjectId(e.target.value);
              setPage(1);
            }}
            placeholder="Filter by project id"
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            User ID
          </label>
          <input
            type="text"
            value={userId}
            onChange={(e) => {
              setUserId(e.target.value);
              setPage(1);
            }}
            placeholder="Filter by user id"
            className="w-full rounded-md border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-3 py-2 text-sm shadow-sm text-gray-900 dark:text-gray-100 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
        <div>
          Total sessions: <span className="font-medium">{pagination.total}</span>
        </div>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={includeArchived}
            onChange={(e) => {
              setIncludeArchived(e.target.checked);
              setPage(1);
            }}
            className="rounded border-gray-300 dark:border-gray-700"
          />
          Include archived
        </label>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-gray-500 dark:text-gray-400 text-sm">Loading sessions...</div>
        </div>
      ) : isError ? (
        <div className="rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          Failed to load sessions.
        </div>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Updated</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Title</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Project</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">User</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-700 dark:text-gray-200">Status</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-700 dark:text-gray-200">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {items.map((chat) => {
                  const project = chat.project || {};
                  const user = chat.user || {};
                  const statusLabel = chat.archivedAt ? 'Archived' : chat.isActive ? 'Active' : 'Inactive';
                  return (
                    <tr key={chat.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">
                        {formatDateTime(chat.updatedAt)}
                      </td>
                      <td className="px-3 py-2 text-gray-900 dark:text-gray-100">
                        <div className="flex flex-col">
                          <span className="font-medium">{chat.title || 'Untitled session'}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{chat.id}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                        <div className="flex flex-col">
                          <span className="text-gray-900 dark:text-gray-100">{project.name || '–'}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{project.id || '–'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                        <div className="flex flex-col">
                          <span className="text-gray-900 dark:text-gray-100">{user.username || '–'}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{user.email || '–'}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">
                        <span className="inline-flex items-center rounded-full bg-gray-50 dark:bg-gray-800 px-2 py-0.5 text-xs font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700">
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right whitespace-nowrap">
                        <Link
                          to={`/admin/chats/${chat.id}`}
                          className="text-blue-600 dark:text-blue-400 hover:underline"
                        >
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}

                {items.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      No sessions found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm">
            <div className="text-gray-500 dark:text-gray-400">
              Page {pagination.page} of {pagination.totalPages || 1}
            </div>
            <div className="space-x-2">
              <button
                type="button"
                onClick={() => canGoPrev && setPage((p) => Math.max(1, p - 1))}
                disabled={!canGoPrev}
                className={`inline-flex items-center rounded border px-3 py-1 ${
                  canGoPrev
                    ? 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                Previous
              </button>
              <button
                type="button"
                onClick={() => canGoNext && setPage((p) => p + 1)}
                disabled={!canGoNext}
                className={`inline-flex items-center rounded border px-3 py-1 ${
                  canGoNext
                    ? 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800'
                    : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                }`}
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminChatsPage;
